import styles from "@shared/components/chat/Messages.module.scss"

import React, { memo, useEffect, useRef, useState } from "react"

import { GROUP } from "@shared/config/group"
import {
  useAccount,
  useChatInfo,
  useLayerPopupContext,
  useMessagesContext,
  useSchedule,
} from "@shared/contexts"
import { useCheckNewMessage, useMessagesRef } from "@shared/hooks"

import classNames from "classnames"

import { useAiIssueCatchStore } from "../../../stores/sidebarStore"

interface Props {
  isActive: boolean
  mention?: React.ReactNode
  inner(props: InnerProps): React.ReactNode
  disableNotice?: boolean
  overflowStyle?: string
  // AI 이슈 캐치/숏폼 패널이 없는 화면(스트리머 채팅 창 등)은
  // 유료 방송이어도 패널 높이만큼 채팅을 줄이지 않고 전체 높이를 쓴다
  fullHeight?: boolean
}

export default memo(function UserMessagesLayout({
  isActive,
  mention,
  inner,
  disableNotice,
  overflowStyle,
  fullHeight,
}: Props) {
  const { chatMenu, isSearchBarEnabled } = useChatInfo()
  const { font } = useLayerPopupContext()
  const { messages, lastStreamerMessage, isMentionAlert } = useMessagesContext()
  const { schedule } = useSchedule()
  const { isOpen } = useAiIssueCatchStore() as { isOpen: boolean }
  const { account } = useAccount()
  const isStreamer = GROUP.STREAMER.includes(account.viewer?.role as string)
  const paymentInformation = schedule?.paymentInformation
  const isAllSchedule = paymentInformation === "ALL"
  const isFreeSchedule = paymentInformation === "FREE"

  const allowFullHeight =
    fullHeight || isAllSchedule || isFreeSchedule || isStreamer

  useCheckNewMessage({
    messages,
    isUser: true,
    isActive:
      isActive ||
      (schedule?.isChatEnabled && chatMenu === "USER") ||
      (!schedule?.isChatEnabled && chatMenu === "MANAGER"),
  })

  const noticeRef = useRef<HTMLDivElement>(null)
  const [noticeHeight, setNoticeHeight] = useState<number>(5)

  const { messagesRef, handleScroll: scrollMessages } = useMessagesRef({
    messages,
    noticeHeight,
    disableAutoScroll: overflowStyle === "hidden", // 가상화 채팅일 경우 외부 강제 스크롤 비활성화
  })

  useEffect(() => {
    if (disableNotice || isSearchBarEnabled || !lastStreamerMessage) {
      setNoticeHeight(5)
      return
    }

    const height = noticeRef.current?.clientHeight

    setNoticeHeight(height || 5)
  }, [lastStreamerMessage, isSearchBarEnabled, disableNotice])

  return (
    <>
      {isMentionAlert && mention}
      <div
        style={{
          height: schedule?.isChatEnabled
            ? allowFullHeight
              ? `100%`
              : isOpen
              ? `calc(100% - 242px)`
              : `calc(100% - 134px)`
            : allowFullHeight
            ? `100%`
            : `calc(100% + 4px)`,
        }}
        className={classNames(
          "chatB",
          isActive ? styles.active : styles.hidden,
        )}
      >
        <div
          className={classNames("inner", styles[font.size])}
          ref={messagesRef}
          style={{
            paddingTop: overflowStyle === "hidden" ? 0 : noticeHeight,
            overflow: overflowStyle,
          }}
        >
          {inner({ scrollMessages, noticeRef, messagesRef, noticeHeight })}
        </div>
      </div>
    </>
  )
})

interface InnerProps {
  scrollMessages(offset: number): void
  noticeRef: React.RefObject<HTMLDivElement>
  messagesRef: React.RefObject<HTMLDivElement>
  noticeHeight: number
}
